import { FC, Fragment } from 'react';

import { CustomLineChartTooltip } from './CustomLineChartTooltip';
import { LineChartSimpleSkeleton } from './LineChartSimpleSkeleton';
import { formatLineChartSimpleDate } from './utils';
import {
  CartesianGrid,
  DotProps,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { v4 as uuidv4 } from 'uuid';

import { IconName } from '@/components/atoms/Icon/types';
import { formatEnpsScoreForDisplay } from '@/utils/enpsDisplay';
import { cn } from '@/utils/tailwind';

import { Card, CardContent } from '../../atoms/Card/Card';
import { TeamPerfomanceCard } from '../TeamPerfomanceCard/TeamPerfomanceCard';

enum ColorEnum {
  LAST_LINE_GREEN = '#51A366',
  LAST_LINE_RED = '#F04438',
  LAST_LINE_NEUTRAL = '#292524',
  LAST_DOT_RED = '#B32318',
  LAST_DOT_GREEN = '#378854',
  YOUR_YEAM_LINE = '#B3E5D2',
  COMPANY_TEAM_COLOR = '#E7E5E4',
}

export type LineChartItem = {
  date: string;
  score?: number;
  companyAverage?: number;
};

type DotPropsType = {
  index: number;
  cx?: number;
  cy?: number;
  payload: LineChartItem & {
    key: string;
  };
};

type LineChartSimpleSize = '1/3' | '2/3' | '3/3';

export type LineChartYAxisScale = 'percentage' | 'index';

const ENPS_Y_AXIS_TICKS = [-100, -50, 0, 50, 100] as const;

const chartHeight: Record<LineChartSimpleSize, number> = {
  '1/3': 238,
  '2/3': 264,
  '3/3': 286,
};

const CHART_VERTICAL_MARGIN_SUM = 10 + 17;

const END_LABEL_VERTICAL_GAP_THRESHOLD_PX = 16;

export type LineChartSimpleProps = {
  data: LineChartItem[];
  companyName: string;
  yourTeamName: string;
  icon?: IconName;
  title?: string;
  size?: LineChartSimpleSize;
  className?: string;
  isLoading?: boolean;
  /** Absent while no score is known yet; the embedded performance card renders an em dash. */
  score?: number;
  comparedToAverage?: number;
  showComparedToAverage?: boolean;
  yAxisScale?: LineChartYAxisScale;
};

export const LineChartSimple: FC<LineChartSimpleProps> = ({
  data,
  title,
  icon,
  companyName,
  yourTeamName,
  size = '2/3',
  className,
  score,
  comparedToAverage,
  isLoading,
  showComparedToAverage = true,
  yAxisScale = 'percentage',
}) => {
  if (isLoading) {
    return <LineChartSimpleSkeleton size={size} />;
  }

  const formattedData = data.map(item => ({
    ...item,
    key: uuidv4(),
    date: formatLineChartSimpleDate(item.date),
  }));

  const dataLastElementIndex = formattedData.length - 1;
  const lastData = formattedData[dataLastElementIndex];
  const secondLastIndex = formattedData.length - 2;
  const secondLastMonthDate = formattedData[secondLastIndex]?.date;
  const secondLastMonthYourTeamValue = formattedData[secondLastIndex]?.score;
  const lastMonthYourTeamValue = formattedData[dataLastElementIndex]?.score;
  const lastMonthCompanyAverageValue = formattedData[dataLastElementIndex]?.companyAverage;

  const plotHeightPx = chartHeight[size] - CHART_VERTICAL_MARGIN_SUM;
  const lastPointVerticalGapPx =
    lastMonthYourTeamValue !== undefined && lastMonthCompanyAverageValue !== undefined
      ? (Math.abs(lastMonthYourTeamValue - lastMonthCompanyAverageValue) / 100) * plotHeightPx
      : undefined;
  const hideCompanyEndLabel =
    showComparedToAverage &&
    lastPointVerticalGapPx !== undefined &&
    lastPointVerticalGapPx < END_LABEL_VERTICAL_GAP_THRESHOLD_PX;

  const indexToStartColouredLineFrom = dataLastElementIndex - 1;
  const cutOff =
    dataLastElementIndex > 0
      ? 100 - ((dataLastElementIndex - indexToStartColouredLineFrom) / dataLastElementIndex) * 100
      : 0;

  const hasTrend =
    lastMonthYourTeamValue !== undefined && secondLastMonthYourTeamValue !== undefined;

  const isYourTeamLastMonthGreaterThanLastSecondMonth =
    hasTrend && lastMonthYourTeamValue > secondLastMonthYourTeamValue;
  const isFlatMonthOverMonth = hasTrend && lastMonthYourTeamValue === secondLastMonthYourTeamValue;

  const lastLineColor = !hasTrend
    ? ColorEnum.YOUR_YEAM_LINE
    : isFlatMonthOverMonth
      ? ColorEnum.LAST_LINE_NEUTRAL
      : isYourTeamLastMonthGreaterThanLastSecondMonth
        ? ColorEnum.LAST_LINE_GREEN
        : ColorEnum.LAST_LINE_RED;

  const lastDotColor = !hasTrend
    ? ColorEnum.YOUR_YEAM_LINE
    : isFlatMonthOverMonth
      ? ColorEnum.LAST_LINE_NEUTRAL
      : isYourTeamLastMonthGreaterThanLastSecondMonth
        ? ColorEnum.LAST_DOT_GREEN
        : ColorEnum.LAST_DOT_RED;

  const gradientId = `gradient-${uuidv4()}`;

  const formatValueLabel = (value: number) =>
    yAxisScale === 'index' ? formatEnpsScoreForDisplay(value) : `${value}%`;

  const renderLegendBySize = () => {
    if (size === '1/3') return null;

    return (
      <div className="hidden md:flex justify-start h-fit sm:justify-end flex-wrap gap-3">
        <div className="flex shrink-0 gap-2">
          <svg width={12} height={12}>
            <circle cx="6" cy="6" r="6" fill={ColorEnum.YOUR_YEAM_LINE} />
          </svg>
          <p className="text-body-12-regular !text-custom-warm-grey-700">{yourTeamName}</p>
        </div>
        {showComparedToAverage && (
          <div className="flex shrink-0 gap-2">
            <svg width={12} height={12}>
              <circle cx="6" cy="6" r="6" fill={ColorEnum.COMPANY_TEAM_COLOR} />
            </svg>
            <p className="text-body-12-regular !text-custom-warm-grey-700">{companyName}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className={cn('p-4 border-custom-warm-grey-150', className)}>
      <CardContent>
        <div className="flex justify-between">
          <TeamPerfomanceCard
            title={title}
            icon={icon}
            score={score}
            comparedToAverage={comparedToAverage}
            size={size}
            className={cn(size === '1/3' ? 'mb-6' : 'mb-4')}
            showComparedToAverage={showComparedToAverage}
            scoreDisplay={yAxisScale === 'index' ? 'enpsScore' : 'percentage'}
          />

          {renderLegendBySize()}
        </div>
        <ResponsiveContainer width="100%" height={chartHeight[size]}>
          <LineChart
            data={formattedData}
            margin={{
              top: 10,
              right: 50,
              bottom: 17,
            }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="100%" y2="0">
                <stop offset="0%" stopColor={ColorEnum.YOUR_YEAM_LINE} />
                <stop offset={`${cutOff}%`} stopColor={ColorEnum.YOUR_YEAM_LINE} />
                <stop offset={`${cutOff}%`} stopColor={lastLineColor} />
                <stop offset="100%" stopColor={lastLineColor} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="12 12" stroke="#F0EEED" vertical={false} />
            <XAxis
              tick={{
                fontSize: '12',
                fontWeight: 400,
                letterSpacing: '-0.01em',
                color: '#A8A29D',
              }}
              dataKey="date"
              axisLine={false}
              textAnchor="middle"
              dy={17}
              tickLine={false}
            />
            <YAxis
              strokeDasharray="3 3"
              domain={yAxisScale === 'index' ? [-100, 100] : [0, 100]}
              ticks={yAxisScale === 'index' ? [...ENPS_Y_AXIS_TICKS] : [0, 25, 50, 75, 100]}
              axisLine={false}
              tickLine={false}
              dx={-12}
              tickFormatter={value => (yAxisScale === 'index' ? String(value) : `${value}%`)}
              tick={{
                fontSize: '12',
                fontWeight: 400,
                letterSpacing: '-0.01em',
                color: '#79716B',
              }}
            />
            <Tooltip
              content={
                <CustomLineChartTooltip
                  companyName={companyName}
                  yourTeamName={yourTeamName}
                  showCompanyAverage={showComparedToAverage}
                  formatValue={yAxisScale === 'index' ? formatEnpsScoreForDisplay : undefined}
                  valueSuffix={yAxisScale === 'percentage' ? '%' : ''}
                />
              }
            />
            <ReferenceLine
              x={secondLastMonthDate}
              stroke="#A8A29D"
              strokeDasharray="12 12"
              strokeWidth={1}
            />
            <ReferenceLine x={lastData?.date} stroke="#A8A29D" strokeWidth={2} />
            {showComparedToAverage && (
              <Line
                type="monotone"
                dataKey="companyAverage"
                strokeLinecap="round"
                strokeWidth={4}
                stroke={ColorEnum.COMPANY_TEAM_COLOR}
                activeDot={({ cx, cy }: DotProps) => {
                  return (
                    <g>
                      <circle cx={cx} cy={cy} r={6} fill={ColorEnum.COMPANY_TEAM_COLOR} />
                      <circle cx={cx} cy={cy} r={4} fill="#FDFDFC" />
                    </g>
                  );
                }}
                dot={({ index, cx, cy, payload }: DotPropsType) => {
                  if (cx === undefined || cy === undefined) return <g />;
                  return index === formattedData.length - 1 ? (
                    <Fragment key={`${payload.key}-company`}>
                      <circle
                        cx={cx}
                        cy={cy}
                        r={4.5}
                        fill={ColorEnum.COMPANY_TEAM_COLOR}
                        stroke="#FFFFFF"
                        strokeWidth={3}
                      />
                      {!hideCompanyEndLabel && payload.companyAverage !== undefined && (
                        <text
                          className="text-body-12-regular !text-custom-warm-grey-500"
                          textAnchor="start"
                          fill="#79716b"
                          x={cx + 8}
                          y={cy + 4}
                        >
                          {formatValueLabel(payload.companyAverage)}
                        </text>
                      )}
                    </Fragment>
                  ) : (
                    <Fragment key={`${payload.key}-company`}></Fragment>
                  );
                }}
              />
            )}
            <Line
              type="monotone"
              dataKey="score"
              strokeLinecap="round"
              strokeWidth={4}
              stroke={`url(#${gradientId})`}
              activeDot={({ cx, cy, index }: DotPropsType) => {
                if (cx === undefined || cy === undefined) return <g />;

                const isLastPoint = index === formattedData.length - 1;
                const activeDotFill = isLastPoint ? lastDotColor : ColorEnum.YOUR_YEAM_LINE;

                return (
                  <g>
                    <circle cx={cx} cy={cy} r={6} fill={activeDotFill} />
                    <circle cx={cx} cy={cy} r={4} fill="#FDFDFC" />
                  </g>
                );
              }}
              dot={({ index, cx, cy, payload }: DotPropsType) => {
                if (cx === undefined || cy === undefined) return <g />;
                return index === formattedData.length - 1 ? (
                  <Fragment key={`${payload.key}-team`}>
                    <circle
                      cx={cx}
                      cy={cy}
                      r={4.5}
                      fill={lastDotColor}
                      stroke="#FFFFFF"
                      strokeWidth={3}
                    />
                    {payload.score !== undefined && (
                      <text
                        className="text-body-12-regular"
                        textAnchor="start"
                        fill={lastDotColor}
                        x={cx + 8}
                        y={cy + 4}
                      >
                        {formatValueLabel(payload.score)}
                      </text>
                    )}
                  </Fragment>
                ) : (
                  <Fragment key={`${payload.key}-team`}></Fragment>
                );
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
